import { Injectable } from '@angular/core';
import { RemoteConfig, fetchAndActivate } from '@angular/fire/remote-config';
import { getBoolean, getString } from 'firebase/remote-config';
import { environment } from '../../environments/environment';

export enum FeatureFlag {
  ENABLE_MOTI_AI = 'enable_moti_ai',
  ENABLE_ANALYTICS = 'enable_analytics',
  ENABLE_MODBUS_BULK_CREATION = 'enable_modbus_bulk_creation',
  ENABLE_BACNET_BULK_CREATION = 'enable_bacnet_bulk_creation',
  ENABLE_LORAWAN_BULK_CREATION = 'enable_lorawan_bulk_creation',
  ENABLE_BULK_TILE_CONFIGURATION = 'enable_bulk_tile_configuration',
  ENABLE_CANVAS_TILE_CREATION = 'enable_canvas_tile_creation',
  ENABLE_LORAWAN_VIEW_POINTS = 'enable_lorawan_view_points',
  ENABLE_FORMULA_TILE_CREATION = 'enable_formula_tile_creation',
  ENABLE_MANAGE_GATEWAY_EUIS = 'enable_manage_gateway_euis',
  ENABLE_CANVAS_CLUSTERING_BY_DEFAULT = 'enable_canvas_clustering_by_default',
  ENABLE_APP_SEARCH_FILTER = 'enable_app_search_filter',
  IOS_UPDATE_VERSION = 'ios_update_version',
  ANDROID_UPDATE_VERSION = 'android_update_version',
  IOS_UPDATE_MESSAGE = 'ios_update_message',
  ANDROID_UPDATE_MESSAGE = 'android_update_message',
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  constructor(private remoteConfig: RemoteConfig) {
    this.initializeFeatureFlags();
  }

  /**
   * Initialize feature flags from Firebase Remote Config
   */
  private async initializeFeatureFlags(): Promise<void> {
    // Set fetch timeout (minimum time between fetches)
    // 10 minutes in production for feature flag updates, 0 in development
    const minimumFetchIntervalMillis = environment.production ? 30000 : 0;

    // Set default values for all feature flags and app configs
    this.remoteConfig.defaultConfig = {
      ...this.remoteConfig.defaultConfig,
      // App update configs
      [FeatureFlag.IOS_UPDATE_VERSION]: '0.0.0',
      [FeatureFlag.ANDROID_UPDATE_VERSION]: '0.0.0',
      // Feature flags
      [FeatureFlag.ENABLE_MOTI_AI]: false,  // Default: AI features disabled
      [FeatureFlag.ENABLE_ANALYTICS]: false,  // Default: Analytics disabled until legal approval
      [FeatureFlag.ENABLE_MODBUS_BULK_CREATION]: false,  // Default: Modbus bulk creation disabled
      [FeatureFlag.ENABLE_BACNET_BULK_CREATION]: false,  // Default: BACnet bulk creation disabled
      [FeatureFlag.ENABLE_LORAWAN_BULK_CREATION]: false,  // Default: LoRaWAN bulk creation disabled
      [FeatureFlag.ENABLE_BULK_TILE_CONFIGURATION]: false,  // Default: Bulk tile configuration disabled
      [FeatureFlag.ENABLE_CANVAS_TILE_CREATION]: false,  // Default: Canvas tile configuration disabled
      [FeatureFlag.ENABLE_LORAWAN_VIEW_POINTS]: false,  // Default: LoRaWAN view points disabled until ready for production
      [FeatureFlag.ENABLE_FORMULA_TILE_CREATION]: true,  // Default: true (override to false in prod Remote Config until ready)
      [FeatureFlag.ENABLE_MANAGE_GATEWAY_EUIS]: false,
      [FeatureFlag.ENABLE_CANVAS_CLUSTERING_BY_DEFAULT]: true,  // Default: clustering on for canvases that haven't set a preference
      [FeatureFlag.ENABLE_APP_SEARCH_FILTER]: true,  // Default: App-view search/tag-filter enabled; flip to false in Remote Config to kill-switch if Product objects to folder/canvas presentation
    };

    try {
      // Set the minimum fetch interval
      this.remoteConfig.settings = {
        ...this.remoteConfig.settings,
        minimumFetchIntervalMillis
      };

      // Attempt to fetch and activate the config
      await fetchAndActivate(this.remoteConfig);
    } catch (error) {
      console.error('Error initializing remote config:', error);
    }
  }

  /**
   * Check if a feature flag is enabled
   */
  public isFeatureEnabled(featureName: FeatureFlag): boolean {
    try {
      return getBoolean(this.remoteConfig, featureName);
    } catch (error) {
      console.error(`Error getting feature flag ${featureName}:`, error);
      return false;
    }
  }

  /**
   * Get a string value from remote config
   */
  public getStringValue(key: FeatureFlag): string {
    try {
      return getString(this.remoteConfig, key);
    } catch (error) {
      console.error(`Error getting string value ${key}:`, error);
      return '';
    }
  }
}
